import type { LiveBreakHost, LiveStreamSegment, NewSegment, SegmentKind } from "@hobbyshopify/backend-client";

export type OtherKind = Exclude<SegmentKind, "BREAK">;

export const OTHER_KINDS: readonly { kind: OtherKind; label: string }[] = [
  { kind: "UNBOXING", label: "Unboxing" },
  { kind: "GUEST", label: "Guest" },
  { kind: "AUTOGRAPHS", label: "Autographs" },
  { kind: "CUSTOM", label: "Something else" },
];

export function segmentKindLabel(kind: SegmentKind): string {
  return kind === "BREAK" ? "Break" : (OTHER_KINDS.find((k) => k.kind === kind)?.label ?? kind);
}

/** A box scheduled further out than this gets a warning before it joins a stream starting now. */
export const BREAK_TIME_WARN_MS = 60 * 60 * 1000;

/** Boxes a stream can take: on sale and in no line-up. */
export function queuedBoxes(breaks: readonly LiveBreakHost[]): LiveBreakHost[] {
  return breaks.filter((b) => b.segment === null && (b.status === "ON_SALE" || b.status === "LOCKED"));
}

export function breaksMuchLater(box: { breakNotBefore: Date | null }, now: Date): boolean {
  return box.breakNotBefore !== null && box.breakNotBefore.getTime() - now.getTime() > BREAK_TIME_WARN_MS;
}

/** The box is scheduled for later than now: drawing it is early. */
export function breaksLater(box: { breakNotBefore: Date | null }, now: Date): boolean {
  return box.breakNotBefore !== null && box.breakNotBefore.getTime() > now.getTime();
}

export interface ExtraSegment {
  kind: OtherKind;
  title: string;
}

/** What the picker adds, boxes first in the order picked, then the rest. */
export function lineupItems(boxIds: readonly string[], extras: readonly ExtraSegment[]): NewSegment[] {
  return [
    ...boxIds.map((breakId) => ({ kind: "BREAK" as const, breakId })),
    ...extras.map((e) => ({ kind: e.kind, title: e.title.trim() })),
  ];
}

export function currentSegment(segments: readonly LiveStreamSegment[]): LiveStreamSegment | null {
  return segments.find((s) => s.status === "CURRENT") ?? null;
}

export function nextPlanned(segments: readonly LiveStreamSegment[]): LiveStreamSegment | null {
  return segments.find((s) => s.status === "PLANNED") ?? null;
}

/** The planned order with one segment moved up (-1) or down (+1); null when it cannot move. */
export function movedPlanned(segments: readonly LiveStreamSegment[], segmentId: string, by: -1 | 1): string[] | null {
  const planned = segments.filter((s) => s.status === "PLANNED").map((s) => s.id);
  const at = planned.indexOf(segmentId);
  const to = at + by;
  if (at < 0 || to < 0 || to >= planned.length) return null;
  const order = [...planned];
  [order[at], order[to]] = [order[to] as string, order[at] as string];
  return order;
}

/** "10:00 AM" today, "Sat 10:00 AM" another day, in the viewer's own time. */
export function formatBreakTime(at: Date, now: Date = new Date()): string {
  const time = at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return at.toDateString() === now.toDateString()
    ? time
    : `${at.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} ${time}`;
}

/** A date as a `datetime-local` input value, in the viewer's own time. */
export function toDateTimeLocal(at: Date | null): string {
  if (!at) return "";
  const local = new Date(at.getTime() - at.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}
